import { useEffect, useRef, useState } from 'react';

export default function CtaSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus('loading');

    try {
      const formDataEncoded = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataEncoded.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d64pvtckdgthdql6tj6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataEncoded.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section ref={sectionRef} id="appointment" className="relative py-28 lg:py-36 bg-neutral-50 overflow-hidden">
      <div className="relative max-w-[1200px] mx-auto px-6 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-px bg-black"></div>
              <span className="text-[11px] font-body font-medium tracking-[0.25em] text-neutral-500 uppercase">
                Book Appointment
              </span>
            </div>

            <h2 className="font-heading text-5xl lg:text-6xl text-black leading-[1] tracking-wide mb-6">
              預約鑑定服務
            </h2>

            <p className="font-body text-[15px] text-neutral-700 leading-[1.8] mb-8">
              填寫表單預約您的專屬鑑定時段，我們的專業團隊將在 24 小時內與您聯繫，為您提供最專業的鑑定服務。
            </p>

            {/* Contact Info */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-black flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-lg text-white"></i>
                </div>
                <div>
                  <div className="font-body text-[12px] text-neutral-500 mb-0.5">Email</div>
                  <div className="font-body text-[14px] text-black">info@tricore.tw</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-black flex items-center justify-center flex-shrink-0">
                  <i className="ri-line-line text-lg text-white"></i>
                </div>
                <div>
                  <div className="font-body text-[12px] text-neutral-500 mb-0.5">LINE</div>
                  <div className="font-body text-[14px] text-black">@tricore</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-black flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-lg text-white"></i>
                </div>
                <div>
                  <div className="font-body text-[12px] text-neutral-500 mb-0.5">營業時間</div>
                  <div className="font-body text-[14px] text-black">週一至週五 10:00-18:00</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className={`transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <form
              id="appointment-form"
              onSubmit={handleSubmit}
              className="bg-white border border-neutral-200 p-8 lg:p-10"
              data-readdy-form
            >
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block font-body text-[12px] text-neutral-600 mb-2 tracking-wide">
                    姓名 *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 text-[14px] text-black placeholder-neutral-400 focus:outline-none focus:border-black transition-colors font-body"
                    placeholder="請輸入您的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block font-body text-[12px] text-neutral-600 mb-2 tracking-wide">
                    電話 *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 text-[14px] text-black placeholder-neutral-400 focus:outline-none focus:border-black transition-colors font-body"
                    placeholder="請輸入您的電話"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block font-body text-[12px] text-neutral-600 mb-2 tracking-wide">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 text-[14px] text-black placeholder-neutral-400 focus:outline-none focus:border-black transition-colors font-body"
                    placeholder="請輸入您的 Email"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block font-body text-[12px] text-neutral-600 mb-2 tracking-wide">
                    鑑定服務 *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 text-[14px] text-black focus:outline-none focus:border-black transition-colors font-body cursor-pointer"
                  >
                    <option value="">請選擇服務項目</option>
                    <option value="鑑寶 LAB - 名牌包鑑定">鑑寶 LAB - 名牌包鑑定</option>
                    <option value="炅白 - 翡翠 & 彩寶裸石">炅白 - 翡翠 & 彩寶裸石</option>
                    <option value="映白 - 培育鑽石 & 水晶">映白 - 培育鑽石 & 水晶</option>
                    <option value="其他諮詢">其他諮詢</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block font-body text-[12px] text-neutral-600 mb-2 tracking-wide">
                    備註說明
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 text-[14px] text-black placeholder-neutral-400 focus:outline-none focus:border-black transition-colors font-body resize-none"
                    placeholder="請描述您的需求（限 500 字）"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={submitStatus === 'loading'}
                  className="w-full px-6 py-4 bg-black text-white font-body text-[14px] font-semibold tracking-wider hover:bg-neutral-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitStatus === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line text-lg animate-spin"></i>
                      送出中...
                    </>
                  ) : (
                    <>
                      送出預約
                      <i className="ri-arrow-right-line text-base"></i>
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <p className="text-[13px] text-black flex items-center gap-2 font-body">
                    <i className="ri-checkbox-circle-line text-lg"></i>
                    預約成功！我們將盡快與您聯繫
                  </p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-[13px] text-red-600 flex items-center gap-2 font-body">
                    <i className="ri-error-warning-line text-lg"></i>
                    送出失敗，請稍後再試
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
